import React, { useEffect, useState } from 'react';
import { Box, Dialog, IconButton, Tooltip, Typography } from '@mui/material';
import { Bone } from '../../../../../../components/ui/DashboardSkeletons';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { toast } from 'react-toastify';
import type { EnrollmentMode, ProgramOwner, ProgramTypeKey } from './types';
import type { RecommendedCourse } from './knowledgeGaps';
import ProgramChat, { ElliottBuildPayload, ProgramFormat, MentorshipPayload } from './add-program-chat/ProgramChat';
import StepNav from './add-program-chat/Stepper';
import ManualEmbeddedBuilder from './ManualEmbeddedBuilder';
import CreationMembersStep from './CreationMembersStep';
import type { AudienceMatchType } from '../../../../utils/memberAttributes';
import { AIMDEmbeddedWizard, mergeWithLocalProgress } from '../../../aimd-v2/AIMDv2Page';
import { INITIAL_WIZARD_STATE, LOCAL_STORAGE_KEY } from '../../../aimd-v2/constants';
import { AIMD_STEPS } from '../../../aimd-v2/types';
import type { AIMDv2WizardState, AIMDStepId } from '../../../aimd-v2/types';
import { saveBuildState, hydrateWizardFromBackend } from '../../../aimd-v2/api';
import useProgramCreationFlowV2 from '../../../../../../hooks/useProgramCreationFlowV2';
// DUMMY: pre-generated Structure + Content so the embedded steps render without
// calling the real generation APIs (which 400 on cloud/APIv3). Remove when wired.

interface Props {
  open: boolean;
  onClose: () => void;
  // Org members eligible as program owners (from useAdminLearningLifecycle).
  existingMembers?: ProgramOwner[];
  // Existing program names — Elliott validates the chosen name against these.
  existingProgramNames?: string[];
  onSubmit: (
    values: AddProgramFormValues,
    intent: 'design-now' | 'design-later'
  ) => void | Promise<void>;
  // Create the group up front when the admin enters Build with Elliott.
  onCreateGroup?: (values: AddProgramFormValues) => Promise<string | null>;
  // Recommended Course review — save the reviewed audience (values + Any/All)
  // to the already-created group before Structure. Resolves false on failure.
  onSaveAudience?: (groupId: string, values: AddProgramFormValues) => Promise<boolean>;
  // Fired when the embedded Structure → Content wizard finishes (publish/Create).
  onElliottComplete?: (groupId: string) => void;
  // Mentorship path: create the group, persist objectives + match settings, and
  // return the new groupId so the modal can embed the Members step.
  onCreateMentorship?: (payload: MentorshipPayload) => Promise<string | null>;
  // 'menu' opens with Elliott offering the framework actions; 'program' (default)
  // starts the Add New Program flow directly.
  entry?: 'program' | 'menu';
  // Org display name (menu copy) + whether the viewer is an Org Admin.
  orgName?: string;
  isOrgAdmin?: boolean;
  // Learning pillars — Elliott asks which pillar when opened from the chat.
  pillars?: { id: string; name: string }[];
  onPillarSelect?: (pillarId: string) => void;
  // Menu actions that leave the chat (close the modal + run the dashboard action).
  onEditFramework?: () => void;
  onBuildFramework?: () => void;
  frameworkBuilt?: boolean;
  // Setup Guide menu option — progress badge + opening the guide.
  setupProgress?: { done: number; total: number };
  onOpenSetupGuide?: () => void;
  // Sync Knowledge — returns whether an org knowledge integration exists.
  onSyncKnowledge?: () => Promise<boolean>;
  // Hides the Sync Knowledge action once a knowledge base is already connected.
  hasKnowledgeIntegration?: boolean;
  // Set when the flow was opened from a Recommended Course card — the chat
  // starts with the recommendation's fields already captured.
  recommendedSeed?: RecommendedCourse | null;
  // Invite Admin — sends the org-scoped admin invitation (Elliott menu flow).
  onInviteAdmin?: (data: { firstName: string; lastName: string; email: string }) => Promise<boolean>;
  onPromoteOrgAdmin?: (userId: string) => Promise<boolean>;
  canInviteOrgAdmin?: boolean;
  // Creation-flow revamp §1 Case A — reopen straight into the embedded wizard
  // for an in-progress AI build (no chat, no recap): the drawer hydrates the
  // saved AIMDv2 build state and lands on the correct early step.
  resumeGroupId?: string | null;
  resumeProgramName?: string;
  // 'members' reopens straight on the program's Members step (Continue on a
  // program waiting at the Invite stage). Default: the embedded wizard.
  resumeMode?: 'wizard' | 'members';
  // Capability release: a Course / Guided Course built with Elliott is built in
  // the Publish Training flow. When given, the conversation asks the type first
  // and hands that build over (see ProgramChat); Manual and Mentorship stay here.
  onBuildWithElliott?: (format: ProgramFormat) => void;
}

// Shape collected by the Add New Program flow. The conversational Elliott flow
// (ProgramChat) gathers exactly these fields, in order, then hands them back to
// the dashboard via onSubmit — same contract the static form used before.
export interface AddProgramFormValues {
  name: string;
  programType: ProgramTypeKey | null;
  audience: string[]; // chosen org member attribute value-ids
  // Any/All per attribute list, keyed by list id (lists absent → "any").
  audienceMatch: Record<string, AudienceMatchType>;
  // Enrollment intent from the audience step — executed by the BE at create.
  enrollmentMode: EnrollmentMode | null;
  launchDate: number | null; // unix seconds
  ownerMode: 'existing' | 'invite' | null;
  existingOwnerId: string | null;
  inviteFirstName: string;
  inviteLastName: string;
  inviteEmail: string;
}

// Build the AIMD wizard state to seed when entering the embedded Structure step.
const seedWizardState = (payload: ElliottBuildPayload): AIMDv2WizardState => ({
  ...INITIAL_WIZARD_STATE,
  currentStep: AIMD_STEPS.indexOf('structure'),
  highestCompletedStep: AIMD_STEPS.indexOf('preview'),
  requirements: {
    programName: payload.programName,
    programDescription: payload.description,
    audience: payload.audience,
    // The Elliott chat collects a flat selection. RequirementsStep re-buckets
    // it into per-list rules once the org catalog is available, so seeding an
    // empty rule set here is correct (and avoids guessing list ownership
    // before the catalog has loaded).
    audienceRules: [],
    tone: payload.tone
  },
  outcomes: { outcomes: payload.outcomes.map((value) => ({ id: crypto.randomUUID(), value })) },
  // Carry selected doc IDs through (toBuildState reads docs flagged selected).
  documents: {
    uploadedDocuments: [],
    previousDocuments: payload.selectedDocIds.map((id) => ({
      id,
      type: 'file' as const,
      title: '',
      fileTypeLabel: 'FILE',
      instructions: '',
      processingStatus: 'indexed' as const,
      isNew: false,
      selected: true
    }))
  },
  preview: { generatedPreview: payload.preview },
  // Structure/Content stay null (INITIAL_WIZARD_STATE) so the embedded steps
  // auto-generate for real. The chat's generation record is carried over so
  // the wizard reuses it instead of creating a duplicate.
  generationId: payload.generationId,
  groupId: payload.groupId
});

// "Add New Program" — an Elliott-led conversation in a central modal. Elliott
// captures the program details, then (for Build with Elliott) the modal switches
// from the conversation into the embedded Structure → Content wizard, locked
// full-screen, without leaving the modal. The framework is blurred behind.
const AddNewProgramDrawer: React.FC<Props> = ({
  open,
  onClose,
  onSubmit,
  onCreateGroup,
  onSaveAudience,
  onElliottComplete,
  onCreateMentorship,
  existingMembers = [],
  existingProgramNames = [],
  entry = 'program',
  orgName,
  isOrgAdmin = true,
  pillars = [],
  onPillarSelect,
  onEditFramework,
  onBuildFramework,
  frameworkBuilt,
  setupProgress,
  onOpenSetupGuide,
  onSyncKnowledge,
  hasKnowledgeIntegration = false,
  recommendedSeed = null,
  onInviteAdmin,
  onPromoteOrgAdmin,
  canInviteOrgAdmin = false,
  resumeGroupId = null,
  resumeProgramName,
  resumeMode = 'wizard',
  onBuildWithElliott
}) => {
  // Expand the modal to (near) full screen, or minimise back to the centred size.
  const [fullScreen, setFullScreen] = useState(false);
  // 'chat' = conversation; 'wizard' = embedded Structure/Content; 'manual' =
  // embedded Manual builder; 'members' = Members step (Manual / Mentorship
  // builds, and Continue at the Invite stage). All but 'chat' are full-screen locked.
  const [mode, setMode] = useState<'chat' | 'wizard' | 'manual' | 'members'>('chat');
  const [wizardGroupId, setWizardGroupId] = useState<string | null>(null);
  const [wizardProgramName, setWizardProgramName] = useState('');
  // The model the embedded Manual builder saves the program as (it has no route to read it from).
  const [manualModel, setManualModel] = useState<'masterclass' | 'masterclass&mentorship'>('masterclass');
  // Shared top-nav stepper index. <0 hides the stepper (menu / invite-admin).
  const [currentStep, setCurrentStep] = useState(entry === 'menu' ? -1 : 0);
  const [resumeLoading, setResumeLoading] = useState(false);
  const creationFlowV2 = useProgramCreationFlowV2();

  // Reopen behaviour. Fresh open: conversation mode at the default size.
  // Resume open (§1 Case A): hydrate the saved build state — the questions the
  // chat would ask are already answered — and land silently in the embedded
  // wizard at the saved early step, full-screen locked like any wizard entry.
  useEffect(() => {
    if (!open) return undefined;
    if (resumeGroupId && resumeMode === 'members') {
      // Nothing to hydrate — the Members step loads the program itself.
      setFullScreen(true);
      setMode('members');
      setCurrentStep(-1);
      setWizardProgramName(resumeProgramName || '');
      setWizardGroupId(resumeGroupId);
      return undefined;
    }
    if (resumeGroupId) {
      let cancelled = false;
      setResumeLoading(true);
      setFullScreen(true);
      setMode('wizard');
      setWizardGroupId(null); // nothing mounts until hydration lands
      setCurrentStep(0);
      (async () => {
        try {
          const beState = await hydrateWizardFromBackend(resumeGroupId);
          if (cancelled) return;
          // Same reconcile as the standalone resume: local cache for THIS
          // group wins when it got further than the BE snapshot.
          const seeded = mergeWithLocalProgress(beState, resumeGroupId);
          localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(seeded));
          setWizardProgramName(resumeProgramName || seeded.requirements?.programName || '');
          setCurrentStep(Math.max(0, Math.min(seeded.currentStep ?? 0, AIMD_STEPS.length - 1)));
          setWizardGroupId(resumeGroupId);
        } catch (err) {
          console.error('[AddNewProgramDrawer] resume hydration failed', err);
          if (!cancelled) {
            toast.error("Couldn't resume this program. Please try again.");
            onClose();
          }
        } finally {
          if (!cancelled) setResumeLoading(false);
        }
      })();
      return () => {
        cancelled = true;
      };
    }
    setFullScreen(false);
    setMode('chat');
    setWizardGroupId(null);
    setCurrentStep(entry === 'menu' ? -1 : 0);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entry, resumeGroupId, resumeMode]);

  // "Review Your Program Structure" → seed the AIMD wizard and switch to the
  // embedded view, locked full-screen. The group already exists (created on entry).
  const handleEnterStructure = (payload: ElliottBuildPayload) => {
    if (!payload.groupId) return;
    setWizardProgramName(payload.programName);
    const seeded = seedWizardState(payload);
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(seeded));
    } catch {
      // ignore quota / serialization issues — the wizard falls back to defaults
    }
    // Best-effort BE persist so the program is resumable later from the dashboard.
    saveBuildState(payload.groupId, seeded).catch(() => {});
    setWizardGroupId(payload.groupId);
    setCurrentStep(AIMD_STEPS.indexOf('structure'));
    setFullScreen(true);
    setMode('wizard');
  };

  // "Build Manually" → seed the ProgramCreation state and switch to the embedded
  // Manual builder, locked full-screen. The group is created on selection.
  const handleEnterManual = (payload: { groupId: string; programFormat: ProgramFormat; programName: string }) => {
    if (!payload.groupId) return;
    const programType = payload.programFormat === 'guided' ? 'guided-masterclass' : 'masterclass';
    const seed = {
      programType,
      buildMode: 'manual',
      groupId: payload.groupId,
      masterclass: { title: payload.programName },
      guidedMasterclass: { title: payload.programName }
    };
    try {
      localStorage.setItem('programCreation', JSON.stringify(seed));
    } catch {
      // ignore — the builder falls back to its defaults
    }
    setManualModel(payload.programFormat === 'guided' ? 'masterclass&mentorship' : 'masterclass');
    setWizardProgramName(payload.programName);
    setWizardGroupId(payload.groupId);
    setCurrentStep(-1); // Manual builder has its own step chrome; hide the chat stepper
    setFullScreen(true);
    setMode('manual');
  };

  // Creation-flow revamp: Manual and Mentorship builds finish on the same
  // Members step as the AI wizard — audience stored, invited on Invite & Finish.
  const enterMembers = (groupId: string, programName?: string) => {
    if (programName !== undefined) setWizardProgramName(programName);
    setWizardGroupId(groupId);
    setCurrentStep(-1); // the Members step carries its own heading
    setFullScreen(true);
    setMode('members');
  };

  // Mentorship → create the group + persist objectives/matches (dashboard).
  // Elliott has already shown the completion message in the chat, so give the
  // admin a moment to read it. Revamp: then the Members step. Legacy: close
  // back to the framework (refetch + highlight the new card).
  const handleEnterMentorship = async (payload: MentorshipPayload) => {
    const groupId = onCreateMentorship ? await onCreateMentorship(payload) : null;
    if (!groupId) return;
    window.setTimeout(() => {
      if (creationFlowV2) enterMembers(groupId, payload.programName);
      else onElliottComplete?.(groupId);
    }, 2600);
  };

  const inWizard = mode === 'wizard';
  const inEmbed = mode !== 'chat'; // wizard / manual — full-screen locked

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      // Backdrop blur + dim on the framework behind the modal
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.55)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)'
          }
        }
      }}
      PaperProps={{
        sx: {
          width: fullScreen ? '96vw' : '100%',
          maxWidth: fullScreen ? '96vw' : 1040,
          height: fullScreen ? '94vh' : 'min(88vh, 720px)',
          m: 2,
          bgcolor: '#0F0F0F', // Fill/Modal/background
          border: '1px solid #444444', // Stroke/Modal/border
          borderRadius: '12px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          transition: 'width 0.2s ease, height 0.2s ease, max-width 0.2s ease'
        }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: '24px',
          py: '20px',
          borderBottom: '1px solid #2A2A2A',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexShrink: 0
        }}
      >
        <Typography
          sx={{
            flex: '1 1 0',
            color: '#FDFDFD',
            fontFamily: 'Inter, sans-serif',
            fontSize: 18,
            fontWeight: 700
          }}
        >
          {/* Program name once in the embedded Structure/Content wizard; the
              "Add New Program" title only during that flow; blank on the menu /
              invite-admin screens. */}
          {inEmbed ? wizardProgramName : currentStep >= 0 ? 'Build a program' : ''}
        </Typography>
        {/* Minimise is hidden once embedded — Structure/Content/Manual stay locked full-screen. */}
        {!inEmbed && (
          <Tooltip title={fullScreen ? 'Minimise' : 'Full screen'}>
            <IconButton
              onClick={() => setFullScreen((v) => !v)}
              aria-label={fullScreen ? 'Minimize' : 'Full screen'}
              sx={{ width: 26, height: 26, p: 0, color: '#FDFDFD' }}
            >
              {fullScreen ? <FullscreenExitIcon sx={{ fontSize: 20 }} /> : <FullscreenIcon sx={{ fontSize: 20 }} />}
            </IconButton>
          </Tooltip>
        )}
        <IconButton onClick={onClose} aria-label="Close Build a program" sx={{ width: 22, height: 22, p: 0 }}>
          <CloseIcon sx={{ color: '#FDFDFD', fontSize: 14 }} />
        </IconButton>
      </Box>

      {/* Shared top-nav stepper — persists across conversation + embedded wizard.
          Hidden on the menu / invite-admin phases (currentStep < 0). */}
      {/* Not over the conversation where it hands AI builds to Publish Training: those steps are that flow's. */}
      {currentStep >= 0 && !(onBuildWithElliott && mode === 'chat') && (
        <StepNav current={currentStep} showMembers={creationFlowV2} />
      )}

      {/* Body — conversation, then the embedded Structure/Content wizard or
          Manual builder. */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {resumeLoading && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px', p: '24px' }}>
            <Bone height={20} width="40%" />
            <Bone height={14} width="70%" />
            <Bone height={120} />
            <Bone height={44} />
          </Box>
        )}
        {open && !inEmbed && !resumeGroupId && (
          <ProgramChat
            existingMembers={existingMembers}
            existingProgramNames={existingProgramNames}
            entry={entry}
            orgName={orgName}
            isOrgAdmin={isOrgAdmin}
            pillars={pillars}
            onPillarSelect={onPillarSelect}
            onEditFramework={onEditFramework}
            onBuildFramework={onBuildFramework}
            frameworkBuilt={frameworkBuilt}
            setupProgress={setupProgress}
            onOpenSetupGuide={onOpenSetupGuide}
            onSyncKnowledge={onSyncKnowledge}
            hasKnowledgeIntegration={hasKnowledgeIntegration}
            recommendedSeed={recommendedSeed}
            onInviteAdmin={onInviteAdmin}
            onPromoteOrgAdmin={onPromoteOrgAdmin}
            canInviteOrgAdmin={canInviteOrgAdmin}
            onSubmit={onSubmit}
            onCreateGroup={onCreateGroup}
            onSaveAudience={onSaveAudience}
            onStepChange={setCurrentStep}
            onEnterStructure={handleEnterStructure}
            onEnterManual={handleEnterManual}
            onEnterMentorship={handleEnterMentorship}
            onBuildWithElliott={onBuildWithElliott}
          />
        )}
        {open && inWizard && wizardGroupId && (
          <AIMDEmbeddedWizard
            groupId={wizardGroupId}
            onFinish={() => onElliottComplete?.(wizardGroupId)}
            onStepChange={(stepId: AIMDStepId) =>
              // Stepper indices align with AIMD_STEPS (Members appended when
              // the creation-flow revamp routes the modal through it).
              setCurrentStep(Math.max(0, AIMD_STEPS.indexOf(stepId)))
            }
          />
        )}
        {open && mode === 'manual' && wizardGroupId && (
          <ManualEmbeddedBuilder
            groupId={wizardGroupId}
            model={manualModel}
            onFinish={() => (creationFlowV2 ? enterMembers(wizardGroupId) : onElliottComplete?.(wizardGroupId))}
            onProgramNameChange={setWizardProgramName}
          />
        )}
        {open && mode === 'members' && wizardGroupId && (
          <CreationMembersStep groupId={wizardGroupId} onFinish={() => onElliottComplete?.(wizardGroupId)} />
        )}
      </Box>
    </Dialog>
  );
};

export default AddNewProgramDrawer;
